/* eslint-disable no-underscore-dangle */
import {
    CommonActions,
    useFocusEffect,
    useNavigation
} from '@react-navigation/native';
import React, {
    useCallback,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Keyboard,
    ScrollView,
    View
} from 'react-native';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import actions from '../../../actions';
import { setSelectedService } from '../../../actions/services';
import { checkRateRequestTime } from '../../../combo';
import {
    InfoList,
    Page,
    Section,
    showNotification,
    TopNotification
} from '../../../common';
import { useServiceListSelector } from '../../../hooks/services';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    ONLINE_BOOKING_CHILD_CLINIC,
    usePersonalData,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../../../hooks/usePersonalData';
import { useStdBackNavigation } from '../../../hooks/useStdBackNavigation';
import { checkNotifications } from '../../../init';
import {
    getUser,
    plannedVisits
} from '../../../selectors';
import { getProviders } from '../../../selectors/consents';
import { getManualVisitors } from '../../../selectors/forms';
import {
    getProviderCapabilities,
    getProviderCodesClinics
} from '../../../selectors/providerCodes';
import {
    getSelectedService,
    getServiceByAssignmentId
} from '../../../selectors/services';
import { gettr } from '../../../selectors/utils';
import { getFinalMessage } from '../../../services/http';
import { storage } from '../../../services/localstorage';
import { isProviderHasCapability } from '../../../utils/consentsUtils';
import {
    dateToDateTimeStrings,
    parseToDdMonYyyy,
    timeslotToDateTime
} from '../../../utils/dates';
import { trimObjectFields } from '../../../utils/strings';
import {
    GET_PAST_VISITS_FROM_FB,
    removeFutureVisit,
    removePastVisit
} from '../../App/snippets/Tests&Visits/utils';
import { RegistryInformation } from '../RegistryInformation';
import { checkConsentReceived } from './checkConsentReceived';
import { checkNearestVisits } from './checkNearestVisits';
import {
    ConsentConfirmationModal,
    getDoNotShowHistoryConsentWarning
} from './ConsentConfirmationModal';
import {
    getError,
    getHintObject
} from './errors';
import { Footer } from './Footer';
import { loadVisitors } from './loadVisitors';
import { makeBooking } from './makeBooking';
import { ProfileSelector } from './ProfileSelector';
import {
    getDefaultProgram,
    getProgramByCovererCode,
    isProgramAllowedToProfile,
    ProgramSelector
} from './ProgramSelector';
import { VisitConsents } from './VisitConsents';

export const getIsProgramActive = (user) => user?.relatives?.find((p) => p.isOwner) || user?.info?.isProgramActive;

const shouldSyncUserStateForChildEnrollConsents = ({
    selectedProfile,
    providerCode,
    cons,
    pendingConsents,
}) => {
    if (selectedProfile?.relKind !== 'CHILD' || !providerCode) {
        return false;
    }

    const profileId = selectedProfile?.id || selectedProfile?.profileId;
    const currentClinicConsents = cons?.family?.[profileId]?.[providerCode] || {};

    const previousOnlineBooking = currentClinicConsents?.[ONLINE_BOOKING_CHILD_CLINIC] === true;
    const previousClinicHistory = currentClinicConsents?.[CHILD_CLINIC_HISTORY_KEY] === true;

    const nextOnlineBooking = pendingConsents?.[ONLINE_BOOKING_CHILD_CLINIC] ?? previousOnlineBooking;
    const previousBothFalse = !previousOnlineBooking && !previousClinicHistory;

    return previousBothFalse && nextOnlineBooking === true;
};

export const EnrollVisitProfiles = Page.register(({ route: { params = {} } }) => {
    const info = useSelector(getUser);
    const {
        visitInfo = {},
        fromScreen,
        savedVisitor,
        savedProgram,
        visiterId,
        canReassign,
        startDate,
        endDate,
        canChangeDate,
        isEditingVisit,
    } = params;
    const {
        assignmentId,
        serviceId,
        orderId,
        covererCode,
        serviceName,
        doctorInfo,
        visitor,
        mdInfo = {},
    } = visitInfo;
    const clinicId = mdInfo?.id;
    const forWho = visiterId || visitor;
    const doctorCoverer = doctorInfo?.coverer;
    const canChangeVisitor = !(canReassign === false);
    const [ profiles, setProfiles ] = useState([]);
    const [ selectedProfile, setSelectedProfile ] = useState();
    const hasManual = useSelector(gettr('db'))?.enableAddPersonOnBooking;
    const selectedService = useSelector(getSelectedService);
    const [ isLoading, setIsLoading ] = useState(false);
    const isSubmittingRef = useRef(false);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const setSubmitting = useCallback((value) => {
        isSubmittingRef.current = value;
        setIsSubmitting(value);
    }, []);
    const currentService = useSelector((state) => getServiceByAssignmentId(state, assignmentId, serviceId));
    const [ error, setError ] = useState();
    const [ isButtonVisible, setButtonVisible ] = useState(true);
    const [ selectedProgram, setSelectedProgram ] = useState();
    const [ timeslotBusyText, setTimeslotBusyText ] = useState('hint.timeslotIsBusy');
    const visits = useSelector(plannedVisits);
    const navigation = useNavigation();
    const userHavePrograms = !!Object.keys(info?.info?.programs ?? {})?.length;
    const dispatch = useDispatch();
    const manualVisitors = useSelector(getManualVisitors);
    const [ consentReceived, setConsentReceived ] = useState(false);
    const [ consentReceivedManual, ] = useState(false);
    const consents = React.useRef({});
    const [ consentError, setConsentError ] = useState(null);
    const [ , setConsentsVersion ] = useState(0);
    const [ skipWarningPopup, setSkipWarningPopup ] = useState(false);
    const clinics = useSelector(getProviders);
    const capabilities = useSelector(getProviderCapabilities);
    const {
        isLoading: isConsentLoading,
        consents: cons,
    } = usePersonalData();
    // const [ isHistoryConsentsProvided, setIsHistoryConsentsProvided ] = useState(false);

    const isChild = useMemo(() => selectedProfile?.relKind === 'CHILD', [ selectedProfile ]);
    const isDisabled = useMemo(() => (selectedProfile?.profileId) && [ 'OTHER',
        'PARENT',
        'SPOUSE' ].includes(selectedProfile?.relKind), [ selectedProfile ]);
    const providers = useSelector(getProviderCodesClinics);
    const providerCode = useMemo(() => providers?.[visitInfo?.branchId ?? clinicId ?? visitInfo?.mdInfo?.id]?.providerCode, [clinicId,
        providers,
        visitInfo?.branchId,
        visitInfo?.mdInfo?.id])

    useStdBackNavigation();

    const selectProfile = useCallback(
        (p = []) => {
            let result;
            const hasAllowedProfiles = p?.some?.((e) => e?.onlineBookingAllowed);
            if (!canChangeVisitor && !selectedProfile) {
                result = p.find((e) => e.profileId === visitInfo?.profileId);
            } else if (savedVisitor) {
                result = p.find((e) =>
                    savedVisitor?.manual
                        ? e?.fullNameRu === savedVisitor?.fullNameRu
                        : e.profileId === savedVisitor.profileId || e?.profileId === savedVisitor?._id,
                );
            } else if (!selectedProfile) {
                if (typeof forWho === 'object') {
                    result = p?.find?.((e) => e?.profileId === forWho.profileId);
                } else {
                    switch (forWho) {
                    case 'forMe':
                        result = p?.find?.(({
                            onlineBookingAllowed,
                            isMe,
                        }) => onlineBookingAllowed && isMe);
                        break;
                    case 'forChild':
                        result = p?.find?.((e) => e?.onlineBookingAllowed && e?.relKind === 'CHILD');
                        break;
                    case 'forRelative':
                        result = p?.find?.(
                            (e) => e?.onlineBookingAllowed && [ 'PARENT', 'SPOUSE' ].includes(e?.relKind),
                        );
                        break;
                    default:
                        result = p?.find((e) => e?.onlineBookingAllowed);
                        break;
                    }
                }
            }
            if (!result && hasAllowedProfiles) {
                result = p.find((e) => e.onlineBookingAllowed);
            }
            (!selectedProfile || (selectedProfile && !selectedProfile?.profileId)) && setSelectedProfile(result);
            const e =
                selectedProfile?.id === 'empty' ? getError(selectedProfile) : getError(result, undefined, orderId);
            setError(e);

            if (savedProgram) {
                setSelectedProgram(savedProgram);
                return true;
            }
            canChangeVisitor && setSelectedProgram(getDefaultProgram(result, info?.info?.programs));
            !canChangeVisitor && setSelectedProgram(getProgramByCovererCode(covererCode, info?.info?.programs, result));

            return false;
        },
        [
            profiles,
            visitInfo,
            setSelectedProfile,
            canChangeVisitor,
            forWho,
            selectedProfile,
            savedVisitor,
            savedProgram,
        ],
    );

    const hasCap = isProviderHasCapability({capabilities, providerCode});
    const isClinicConsentProvided = !hasCap
        || (!isChild && (
            cons?.user?.[providerCode]?.[USER_CLINIC_HISTORY_KEY] || consents.current?.consents?.[USER_CLINIC_HISTORY_KEY]
        ))
        || (isChild && (
            cons.family[selectedProfile?.id]?.[providerCode]?.[CHILD_CLINIC_HISTORY_KEY] || consents.current?.consents?.[CHILD_CLINIC_HISTORY_KEY]));

    const isConsentProvided = (!isChild && (cons?.user?.main?.[USER_HISTORY_KEY] || consents.current?.consents?.[USER_HISTORY_KEY]))
        || (isChild &&
            (cons.family[selectedProfile?.id]?.main?.[CHILD_HISTORY_KEY] || consents.current?.consents?.[CHILD_HISTORY_KEY]));
    const isAllHistoryConsentProvided = isClinicConsentProvided && isConsentProvided;

    useFocusEffect(useCallback(() => {
        getDoNotShowHistoryConsentWarning()
            .then((result) => {
                setSkipWarningPopup(result);
            })
            .catch(() => setSkipWarningPopup(false));
    }, []));

    const startLoadVisitors = useCallback(async () => {

        const fetchData = async () => {
            const fromCache = !!(savedProgram || savedVisitor);
            // const listLength = filterAdultChild(profiles?.filter(p => !p?.manual))?.length ?? 0;
            if (!isSubmittingRef.current) {
                setIsLoading(!fromCache);
            }
            try {
                const p = await loadVisitors({
                    info,
                    assignmentId,
                    serviceId: selectedService?.id ?? serviceId,
                    requestBookCapabilities: canChangeVisitor || userHavePrograms,
                    cacheRequest: fromCache,
                    // listLength,
                    manualVisitors,
                });
                setProfiles(p);
                const setTheProgram = selectProfile(p);
                if (visitInfo?.covererCode && !canChangeVisitor && !setTheProgram) {
                    const found =
                    savedProgram ||
                    Object.entries(info?.info?.programs ?? {})
                        ?.find
                        ?.(
                            ([ , value ]) =>
                                value.covererCode === visitInfo.covererCode &&
                                isProgramAllowedToProfile(p, value?.programId, visitInfo?.profileId),
                        );
                    if (found) {
                        setSelectedProgram({
                            id: found[0],
                            ...found[1],
                        });
                    }
                }
            } catch (error) {
                void error;
            } finally {
                if (!isSubmittingRef.current) {
                    setIsLoading(false);
                }
            }
        }

        await fetchData();
    }, [info,
        // info?.relatives?.length,
        assignmentId,
        serviceId,
        savedProgram,
        savedVisitor]);

    useFocusEffect(useCallback(() => {
        startLoadVisitors();
    }, [startLoadVisitors]));

    useServiceListSelector(assignmentId, !(selectedService?.id || (serviceId && serviceId !== '0')));

    const infoFields = useMemo(
        () => {
            const converted = dateToDateTimeStrings(new Date(startDate));
            return {
                ...visitInfo,
                date: converted?.date ?? parseToDdMonYyyy(visitInfo?.date ?? ''),
                service: selectedService?.name || serviceName,
                serviceDescription: selectedService?.description || currentService?.description,
                time: converted?.time ?? visitInfo?.time,
                //                ...(timeslotToDateTime(visitInfo?.timetableId)),
            };
        },
        [startDate,
            visitInfo,
            selectedService?.name,
            selectedService?.description,
            serviceName,
            currentService?.description]
        ,
    );

    const isButtonDisabled = useCallback(() => {
        return !error?.allowBooking || (!selectedProfile && !orderId) || !consentReceived;
    }, [ error?.allowBooking,
        selectedProfile,
        orderId,
        consentReceived ]);

    const doSubmit = useCallback(async () => {
        const shouldSyncUserAfterConsentUpdate = shouldSyncUserStateForChildEnrollConsents({
            selectedProfile,
            providerCode: consents.current?.providerCode,
            cons,
            pendingConsents: consents.current?.consents,
        });

        setSubmitting(true);

        const {
            received,
            error,
        } = await checkConsentReceived({
            consents: consents.current,
            dispatch,
            profileId: selectedProfile?.profileId,
            clinics,
            loadingDelay: 0,
        });
        if (!received) {
            setSubmitting(false);
            setConsentError(error ?? {
                level: 'error',
                message: Object.R('errors.ErrorSetConsent'),
            });
            return;
        }

        checkNearestVisits({
            onDeferred: () => setSubmitting(false),
            startDate,
            endDate,
            visits,
            profile: selectedProfile,
            visitInfo,
            navigation,
            canReassign,
            canChangeDate,
            isEditingVisit,
            onBook: async () => {
                const wasNPAsked = await storage.get('wasNPAsked');
                makeBooking({
                    consentReceived,
                    manualConsent: consentReceivedManual,
                    info: info?.info,
                    profile: selectedProfile,
                    orderId,
                    onError: (e) => {
                        if (e?.status === 409) {
                            setButtonVisible(false);
                            setError({
                                message: (e.body || {}).detail,
                                allowBooking: false,
                                level: 'error',
                                code: undefined,
                                noSupport: true,
                            });
                            setTimeslotBusyText((e.body || {}).detail);
                        } else if (e.code === 'CODE_VISITOR_DATA_MISMATCH') {
                            Page.showModal(
                                <RegistryInformation
                                    mdInfo={visitInfo?.mdInfo}
                                    errorHint={getFinalMessage(e.body)}
                                    errorTitle={'titles.changeVisit'}
                                    startDate={visitInfo?.startDate ?? visitInfo?.date}
                                    time={visitInfo?.time}
                                />,
                            );
                        } else if (e?.status === 423) {
                            navigation.goBack();
                            showNotification(
                                `${(e.body || {}).detail}\n${visitInfo?.mdInfo?.phones?.join?.('\n')}`,
                                false,
                            );
                        } else if (e?.code === 'MISSING_CONSENT') {
                            setError({
                                message: getFinalMessage(e.body),
                                allowBooking: true,
                                level: 'error',
                            });
                        } else {
                            setError({
                                message: getFinalMessage(e.body),
                                allowBooking: false,
                                level: 'error',
                            });
                        }
                    },
                    onLoadingState: setSubmitting,
                    selectedService,
                    serviceId,
                    program: selectedProgram,
                    assignmentId,
                    visitInfo,
                    onSuccess: () => {
                        if (!GET_PAST_VISITS_FROM_FB && orderId && isEditingVisit) {
                            removeFutureVisit(orderId);
                            removePastVisit(orderId);
                        }
                        setError();
                        if (fromScreen === 'DoctorRecordDatePick') {
                            navigation?.pop(1);
                        }
                        dispatch(setSelectedService());
                        actions.setSelectedService();
                        // After a successful booking, redirect to the home screen.
                        navigation.dispatch(
                            CommonActions.reset({
                                index: 0,
                                routes: [ { name: 'Home' } ],
                            }),
                        );

                        if (shouldSyncUserAfterConsentUpdate) {
                            //syncUserState();
                        }

                        if (!wasNPAsked) {
                            checkNotifications('From visit', () => {
                                if (!isEditingVisit) {
                                    checkRateRequestTime(() => {
                                        setTimeout(
                                            () =>
                                                showNotification(
                                                    Object.R('titles.successfulBooking', {
                                                        date: parseToDdMonYyyy(visitInfo?.date ?? ''),
                                                        time: visitInfo?.time,
                                                        ...(timeslotToDateTime(visitInfo?.timetableId)),
                                                    }),
                                                ),
                                            100,
                                        );
                                    });
                                }
                            });
                        } else {
                            setTimeout(
                                () =>
                                    showNotification(
                                        Object.R('titles.successfulBooking', {
                                            date: parseToDdMonYyyy(visitInfo?.date ?? ''),
                                            time: visitInfo?.time,
                                            ...(timeslotToDateTime(visitInfo?.timetableId)),
                                        }),
                                    ),
                                100,
                            );
                        }
                        if (!isEditingVisit) {
                            checkRateRequestTime(() => {
                                showNotification(
                                    Object.R('titles.successfulBooking', {
                                        date: parseToDdMonYyyy(visitInfo?.date ?? ''),
                                        time: visitInfo?.time,
                                        ...(timeslotToDateTime(visitInfo?.timetableId)),
                                    }),
                                );
                            });
                        }
                        // consoleLog('success booking', param);
                    },
                });
            },
        });

    }, [ assignmentId,
        canChangeDate,
        canReassign,
        clinics,
        cons,
        consentReceived,
        consentReceivedManual,
        dispatch,
        endDate,
        fromScreen,
        info?.info,
        isEditingVisit,
        navigation,
        orderId,
        selectedProfile,
        selectedProgram,
        selectedService,
        serviceId,
        startDate,
        visitInfo,
        visits ]);

    const onSubmit = useCallback(async () => {
        if (isAllHistoryConsentProvided || skipWarningPopup) {
            doSubmit();
        } else {
            Page.showModal(<ConsentConfirmationModal onContinue={() => {
                doSubmit();
            }}/>);
        }
    }, [isAllHistoryConsentProvided,
        skipWarningPopup,
        doSubmit]);

    const onProfileSelect = useCallback(
        (v) => {
            setSelectedProfile(v);
            const defaultProgram = getDefaultProgram(v, info?.info?.programs);
            setSelectedProgram(defaultProgram);
            setError(getError(v, undefined, orderId));
            navigation.setParams({ savedVisitor: v });
            navigation.setParams({ savedProgram: defaultProgram });
        },
        [ selectedProfile,
            error,
            setError ],
    );

    const onChangeProgramHandler = (program) => {
        setSelectedProgram(program);
        navigation.setParams({ savedProgram: program });
    };

    const getHint = useCallback(() => {
        return getHintObject({
            profile: selectedProfile,
            coverer: doctorCoverer,
            covererCode,
            ownerHasActiveProgram: getIsProgramActive(info?.info),
        });
    }, [ selectedProfile,
        doctorCoverer,
        covererCode,
        info?.info,
        timeslotBusyText ]);

    // const onConsentHandler = (received, manual) => {
    //     setConsentReceived(received);
    //     setConsentReceivedManual(manual);
    // };

    const isPageBusy = isSubmitting || isLoading || isConsentLoading;

    return (
        <>
            <Page
                isLoading={isPageBusy}
                name={'enroll-visit'}
                hint={getHint()}
                noscroll
                persistKeyboard
            >
                {!isPageBusy && (
                    <ScrollView
                        style={{ flex: 1 }}
                        contentContainerStyle={{ flexGrow: 1, justifyContent: 'space-between' }}
                        keyboardShouldPersistTaps="handled"
                        nestedScrollEnabled={true}
                        onScrollBeginDrag={() => Keyboard.dismiss()}
                    >
                        <View>
                            <ProfileSelector
                                data={profiles}
                                selected={selectedProfile}
                                onChange={onProfileSelect}
                                hasManual={hasManual && canChangeVisitor}
                                isLoading={isLoading}
                                isError={error?.allowBooking === false}
                                enabled={canChangeVisitor}
                                orderId={orderId}
                            />
                            <ProgramSelector
                                profile={selectedProfile}
                                onChange={onChangeProgramHandler}
                                programs={info?.info?.programs}
                                isLoading={isLoading}
                                selected={selectedProgram}
                            />
                            <Section
                                title="titles.details_of_visit_header"
                                flex
                                justify="space-between"
                                textStyles={{
                                    color: '#555555',
                                    marginBottom: 12,
                                }}
                            >
                                <InfoList
                                    fields="enrollVisitInfoFields"
                                    data={trimObjectFields(infoFields)}
                                />
                            </Section>
                            {!!selectedProfile && <VisitConsents
                                isChild={isChild}
                                disabled={isDisabled}
                                branchId={visitInfo?.mdInfo?.branchId || visitInfo?.mdInfo?.id}
                                profileId={selectedProfile?.profileId}
                                onConsentsReceived={(received, c) => {
                                    const hasChanged = JSON.stringify(consents.current) !== JSON.stringify(c);
                                    consents.current = c;
                                    if (hasChanged) {
                                        setConsentError();
                                        setConsentsVersion(prev => prev + 1);
                                    }
                                    setConsentReceived(received);
                                }}/>}
                            {!!consentError && <TopNotification hint={consentError}/>}
                        </View>
                        <Footer
                            isButtonDisabled={isButtonDisabled()}
                            onSubmit={onSubmit}
                            isButtonVisible={isButtonVisible}
                            error={
                                !isButtonVisible
                                    ? {
                                        message: timeslotBusyText,
                                        level: 'error',
                                    }
                                    : error
                            }
                            isLoading={isLoading}
                        />
                    </ScrollView>
                )}
            </Page>
        </>
    );
});
